import { Locale } from '@/types'
import Cookies from 'js-cookie'
import { CookieName } from '@/config/cookies'
import endpoints from '@/config/endpoints'

class CSEService {
  private cgosCompanies: Set<string> = new Set(['cgos', 'CGOSPM', 'cgosaura'])
  private cgosOrCGOSPM: Set<string> = new Set(['cgos', 'CGOSPM'])

  get isCSE(): boolean {
    return this.getLocale() === 'fr-FR-CSE'
  }

  get isAnyCGOS(): boolean {
    const company: string | undefined = Cookies.get(CookieName.COMPANY)
    if (!company || !this.isCSE) return false
    return this.cgosCompanies.has(company)
  }

  get isCgosOrCGOSPM(): boolean {
    const company: string | undefined = Cookies.get(CookieName.COMPANY)
    if (!company || !this.isCSE) return false
    return this.cgosOrCGOSPM.has(company)
  }

  protected getLocale(): Locale {
    return window.env.VUE_APP_LOCALE as Locale
  }

  /**
   * Fetches company data from the API.
   * @returns The company data or `null` if an error occurs.
   */
  private async fetchCompanyData(): Promise<any | null> {
    const companyName = Cookies.get(CookieName.COMPANY)

    if (!companyName) {
      console.error('Company name is missing from cookies.')
      return null
    }

    const companyUrl = `${endpoints.companyData}?companyName=${encodeURIComponent(companyName)}`

    try {
      const response = await fetch(companyUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json, text/plain, */*',
          authorization:
            Cookies.get('user-refresh-token') || '<fallback-token>',
        },
      })

      if (!response.ok) {
        console.error('Failed to fetch company data.')
        return null
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching company data:', error)
      return null
    }
  }

  /**
   * Sets the companyLang cookie based on the language field from the API response.
   */
  public async setCompanyLang(): Promise<void> {
    if (!this.isCSE) return

    const companyName = Cookies.get(CookieName.COMPANY)
    if (!companyName?.trim()) return

    const companyData = await this.fetchCompanyData()
    if (!companyData) return

    const language = companyData?.language
    if (!language) {
      console.error('Language is missing in the company data.')
      return
    }

    Cookies.set('companyLang', language, {
      path: '/',
      sameSite: 'None',
      secure: true,
    })
  }

  /**
   * Fetches encoded credentials from the API and appends them to the target URL hash.
   * @param targetUrl The URL to which credentials should be appended.
   * @returns The updated URL with the credentials appended, or `null` if an error occurs.
   */
  public async getRedirectUrlWithCredentials(
    targetUrl: string
  ): Promise<string | null> {
    if (this.isAnyCGOS) {
      const targetUrlObject = new URL(targetUrl, window.location.origin)
      const encodedRedirectTo = encodeURIComponent(
        targetUrlObject.pathname + targetUrlObject.search + targetUrlObject.hash
      )

      const companyName = Cookies.get(CookieName.COMPANY) || ''
      const loginUrl = endpoints.login.replace('${company}', companyName)
      return `${loginUrl}?redirectTo=${encodedRedirectTo}`
    }

    const companyData = await this.fetchCompanyData()
    if (!companyData) return null

    const encodedCredentials = companyData?.encodedCredentials
    if (!encodedCredentials) {
      console.error('Encoded credentials are missing in the company data.')
      return null
    }

    const targetUrlObject = new URL(targetUrl, window.location.origin)
    const encodedRedirectTo = encodeURIComponent(
      targetUrlObject.pathname + targetUrlObject.search + targetUrlObject.hash
    )

    const companyName = Cookies.get(CookieName.COMPANY) || ''
    const loginUrl = endpoints.login.replace('${company}', companyName)
    return `${loginUrl}?credentials=${encodedCredentials}&redirectTo=${encodedRedirectTo}`
  }
}

export default CSEService
